import { useCallback, useEffect, useRef, useState } from 'react';
import {
  convertWeight,
  defaultBarFor,
  defaultInventoryFor,
  roundWeight,
  type PlateInventoryEntry,
} from '@heraion/shared';

import {
  loadPlateBar,
  loadPlateInventory,
  persistPlateBar,
  persistPlateInventory,
} from './state/plateConfig';
import type { WeightUnit } from './state/loggerDraft';

// The bar and the gym's plate inventory, split out of `usePlateCalcModalState`
// (LG-91 review) because they share one rule that belongs in one place: WHO
// WINS when several sources name the bar.
//
// ⭐ LG-91: the athlete's pick this open > the catalog `defaultBarKg` > the
// saved bar > the unit fallback. The async read of the saved bar must not
// clobber the first two (`barTouched`, `catalogBarKg`).
//
// Both are stored PER UNIT: a pound gym's rack is not the metric one
// converted, so switching the row's unit reads the other gym's iron rather
// than re-expressing this one's.
//
// What stays in the parent on purpose: everything that touches the working
// value or the composition. Those are the other half of "the plates on the
// bar", and the parent is the only one that sees both.
export function usePlateBarInventory(
  unit: WeightUnit,
  catalogBarKg: number | null,
) {
  const [bar, setBar] = useState<number>(() => defaultBarFor(unit));
  const [inventory, setInventory] = useState<PlateInventoryEntry[]>(() =>
    defaultInventoryFor(unit).map((e) => ({ ...e })),
  );
  // Set once the athlete picks a chip this open; cleared by `seedBar`/`release`.
  const barTouched = useRef(false);

  // Open: forget the last pick and, when the exercise names a bar, take it.
  //
  // ⚠️ The catalog field is kilos by name and by storage, so a pound gym gets
  // it converted, a 7 kg EZ bar really is 15.4 lb, and that is the honest
  // number even though no chip in `BAR_OPTIONS.lb` reads 15.4. Naming that bar
  // in its own unit is the custom-bar work of fetta E2.
  const seedBar = useCallback(() => {
    barTouched.current = false;
    if (catalogBarKg !== null) {
      setBar(
        roundWeight(convertWeight(catalogBarKg, 'kg', unit), unit, 'display'),
      );
    }
  }, [unit, catalogBarKg]);

  // Closed: a pick must not survive into the next exercise's open.
  const releaseBar = useCallback(() => {
    barTouched.current = false;
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [b, inv] = await Promise.all([
        loadPlateBar(unit),
        loadPlateInventory(unit),
      ]);
      if (cancelled) return;
      // LG-91: a fresh pick or a catalog bar outranks the saved one.
      if (!barTouched.current && catalogBarKg === null) setBar(b);
      setInventory(inv);
    })();
    return () => {
      cancelled = true;
    };
  }, [unit, catalogBarKg]);

  const pickBar = (value: number) => {
    barTouched.current = true;
    setBar(value);
    void persistPlateBar(unit, value);
  };

  const setPairs = (size: number, pairs: number) => {
    const clamped = Math.max(0, Math.min(99, Math.floor(pairs)));
    setInventory((prev) => {
      const existing = prev.find((p) => p.size === size);
      const next = existing
        ? prev.map((p) => (p.size === size ? { ...p, pairs: clamped } : p))
        : [...prev, { size, pairs: clamped }].sort((a, b) => b.size - a.size);
      void persistPlateInventory(unit, next);
      return next;
    });
  };

  return { bar, inventory, seedBar, releaseBar, pickBar, setPairs };
}
